// The game: a map, the road, the enemy coming down it and the towers the player puts beside it.
// The sim (sim/src/td.ts) runs here at 20 ticks a second, as fast as the speed button says; this
// mode draws its snapshots (the 3D scene does the heavy lifting), takes the clicks, and keeps the
// HUD: the shop down the right, the selected tower's card bottom left, the abilities down the left,
// the wave and the cash across the top, and the big Start wave button bottom right.
import { Container, Graphics } from 'pixi.js';
import { ABILITY_KINDS, DEFAULT_CONFIG, MAP_SIZE, MAX_TOWER_LEVEL, PLACE_ERROR_TEXT, PRICE, TARGET_MODES, TARGET_MODE_NAMES, THREAT_NAMES, TICKS_PER_SECOND, TILE, TOWERS, TdSim, damageAt, rangeAt, waveBonus, EARLY_CALL_BONUS_PCT, type AbilityKind, type Snapshot, type TargetMode, type TdCommand, type TdMap, type Tower } from '@thrash/sim';
import type { Camera } from './camera.ts';
import { paintBars } from './bars.ts';
import { toScreen, toTile } from './iso.ts';
import { DEATH_MS, FOOT, MISSILE_LAUNCHERS, SMOKE_MS, buildingsSignature, drawIncoming, paintBuildingFx, paintBuildings, paintDeaths, paintGrid, paintShots, paintSmoke, paintUnits, shotLife, snapshotToDrawables, type DeathFx, type DrawableBuilding, type LiveShot, type SmokePuff } from './render.ts';
import { is3D, setRoad3D } from './view3d.ts';
import { button, el, toast } from './hud.ts';
import { abilityIcon, icon } from './icons.ts';
import { NUCLEAR, feedEvents, feedNote, resetFeed } from './feed.ts';
import { hideConsole, showUnit } from './console.ts';
import { hideTag } from './tag.ts';
import { haptic } from './haptics.ts';
import { announceNuke, cramFiring, playAlarmBell, playCannon, playDeathCry, playExplosion, playGore, playIntercept, playLaunch, playNuke, playRifle, playSiren, playSniper } from './sound.ts';
import { bestWaves, clearSave, recordWaves, storeSave } from './progress.ts';
import { portraitEl } from './portraits.ts';

const TICK_MS = 1000 / TICKS_PER_SECOND;
const SPEEDS = [1, 2, 3] as const;
/** How long the next wave waits when it comes by itself. */
const AUTO_MS = 4000;

/** One line on each tower, for the shop and its card. */
const ROLE: Record<string, string> = {
  mg_nest: 'Fast fire at anything on the ground. The first gun you buy.',
  sniper_nest: 'One huge round every three seconds, anywhere on the map, at infantry only.',
  land_mine: 'Goes on the road: one blast under the first foot soldier who steps on it.',
  bunker: 'A concrete squad: steady rifle fire, and it shrugs off small arms.',
  at_mine: 'Goes on the road: one blast under the first vehicle over it.',
  atgm_post: 'Guided missiles that wreck armour from ten tiles. Nothing for infantry.',
  howitzer: 'Long-range 155 mm shells that blast every ground unit round the strike. Thirty rounds a wave.',
  drone_interceptor: 'Airburst cannon that swats drones out of the sky. Only drones.',
  air_defence: 'Guns and missiles against helicopters and drones.',
  c_ram: 'A gatling stream right across the map at everything in the air and some things on the ground. It runs dry.',
  missile_defence: 'Iron Dome: interceptors for cruise missiles.',
  patriot: 'Interceptors for ballistic and cruise missiles, and aircraft.',
  thaad: 'The only answer to a glide vehicle; takes ballistic missiles too.',
  tank_depot: 'A T-90 that roams six tiles round its depot and shells whatever walks or rolls; shot down, it is back in twenty seconds.',
  helipad: 'A Ka-52 that hunts nine tiles round its pad: men, armour, drones and aircraft. It draws fire from your towers.',
  airbase: 'A fighter off a carrier offshore, circling eighteen tiles out and shooting down whatever flies, cruise missiles included.',
  missile_battery: 'Anti-ship missiles with the reach of the whole bay. Twenty of them.',
  bastion: 'Supersonic anti-ship missiles: eight of them, each enough to sink a boat and bend a frigate.',
};

/** A tower's weapon in a line: damage a second, its reach in tiles, what it shoots. */
function weaponLine(t: Tower): string {
  const def = t.def;
  if (def.mine) return `${def.damage} blast · ${THREAT_NAMES[def.targets![0]!]}`;
  if (def.patrol) { const u = DEFAULT_CONFIG.units[def.patrol.unit]; return `${u?.name ?? def.patrol.unit} on patrol · ${def.patrol.radius / TILE} tiles round it`; }
  if (!def.damage || !def.attackTicks) return '';
  const burst = def.burst ?? 1;
  const cycle = (burst - 1) * (def.burstGap ?? 1) + def.attackTicks;
  const dps = Math.round((damageAt(def, t.level) * burst * TICKS_PER_SECOND) / cycle);
  const range = rangeAt(def, t.level) / TILE;
  const reach = range >= 60 ? 'whole map' : `${Math.round(range * 10) / 10} tiles`;
  const splash = def.splash ? ` · ${(def.splash / TILE).toFixed(1)}-tile blast` : '';
  return `${dps} dmg/s · ${reach}${splash}`;
}

interface Sel { kind: 'tower' | 'enemy'; id: number }

export interface DefenseCallbacks {
  onHome(): void;
}

export class DefenseMode {
  readonly root = new Container();
  /** The road in the 2D look; the 3D look lays its own. */
  private readonly roadLayer = new Graphics();
  private readonly buildingLayer = new Graphics();
  private readonly fxLayer = new Graphics();
  private readonly ghostLayer = new Graphics();
  private readonly unitLayer = new Graphics();
  private readonly shotLayer = new Graphics();
  private readonly deathLayer = new Graphics();
  private sim!: TdSim;
  private map!: TdMap;
  private prev!: Snapshot;
  private next!: Snapshot;
  private acc = 0;
  private speed: (typeof SPEEDS)[number] = 1;
  private paused = false;
  /** A tower picked in the shop and not yet put down. */
  private placing: string | null = null;
  /** The tile under the pointer. */
  private hover: [number, number] | null = null;
  private sel: Sel | null = null;
  private aiming: AbilityKind | null = null;
  private liveShots: LiveShot[] = [];
  private deaths: DeathFx[] = [];
  private smoke: SmokePuff[] = [];
  /** When each tower last fired, by id, for the C-RAM's spin. */
  private readonly firedAt = new Map<number, number>();
  private drawnSignature = '';
  private lastSync = 0;
  /** The result card for the win is shown once; the game goes on past it. */
  private wonShown = false;
  private lost = false;
  /** The next wave starts by itself a few seconds after the last is held. */
  private auto = false;
  /** When the next wave starts by itself, ms; 0 when nothing is counting down. */
  private autoAt = 0;
  /** The game tick last saved. */
  private savedTick = -1;
  /** Every tick of the wave that is on (or was last on), for the scrub bar: the view can be dragged back through it. */
  private record: Snapshot[] = [];
  /** Where the scrub bar is, as an index into the record, or -1 while the game runs live. */
  private scrubAt = -1;
  private scrub: HTMLInputElement | null = null;
  private scrubLabel: HTMLElement | null = null;
  /** Floating numbers over the map: cash from a kill, the bite of a leak. */
  private readonly floats = ((): HTMLElement => { let f = document.getElementById('floats'); if (!f) { f = el('div'); f.id = 'floats'; document.body.appendChild(f); } return f; })();
  private panelParts: { cash: HTMLElement; wave: HTMLElement; sub: HTMLElement; hint: HTMLElement; integrity: HTMLElement; bar: HTMLElement; shop: Map<string, HTMLButtonElement>; abilities: Map<AbilityKind, HTMLButtonElement>; selHp: HTMLElement | null; upgrade: HTMLButtonElement | null; rebuild: HTMLButtonElement | null; preview: HTMLElement; start: HTMLButtonElement | null } | null = null;

  constructor(private readonly camera: Camera, private readonly panel: HTMLElement, private readonly hud: Graphics, private readonly cb: DefenseCallbacks) {
    this.root.addChild(this.roadLayer, this.buildingLayer, this.fxLayer, this.deathLayer, this.ghostLayer, this.unitLayer, this.shotLayer);
  }

  get active(): boolean {
    return !!this.sim;
  }

  get finished(): boolean {
    return this.lost;
  }

  get mapInPlay(): TdMap | null {
    return this.sim ? this.map : null;
  }

  /** A new game on a map. */
  start(map: TdMap, seed = (Date.now() % 0x7fffffff) >>> 0): void {
    this.begin(map, new TdSim(map, seed));
  }

  /** A saved game, played back from its log to where it was. */
  restore(map: TdMap, seed: number, log: readonly TdCommand[], tick: number): void {
    this.begin(map, TdSim.replay(map, seed, log, tick));
  }

  private begin(map: TdMap, sim: TdSim): void {
    this.map = map;
    this.sim = sim;
    this.prev = this.next = this.sim.snapshot();
    this.acc = 0;
    this.speed = 1;
    this.paused = false;
    this.placing = null;
    this.hover = null;
    this.sel = null;
    this.aiming = null;
    this.liveShots = [];
    this.deaths = [];
    this.smoke = [];
    this.firedAt.clear();
    this.drawnSignature = '';
    this.wonShown = sim.won;
    this.lost = sim.phase === 'lost';
    this.auto = false;
    this.autoAt = 0;
    this.savedTick = -1;
    this.record = [];
    this.scrubAt = -1;
    this.floats.replaceChildren();
    setRoad3D(map);
    this.paintRoad();
    resetFeed();
    feedNote(sim.tick > 0 ? `${map.name.toUpperCase()} · WAVE ${sim.wave} · CONTINUED` : `${map.name.toUpperCase()} · HOLD ${map.waves} WAVES`, 'good', '★');
    this.renderPanel();
    this.save();
  }

  /** Keeps the game in the browser: its log replays to this exact state. */
  private save(): void {
    if (!this.sim || this.lost) return;
    if (this.sim.tick === this.savedTick) return;
    this.savedTick = this.sim.tick;
    storeSave({ mapId: this.map.id, seed: this.sim.seed, log: this.sim.log, tick: this.sim.tick, wave: this.sim.wave });
  }

  /** The ground and the road in the 2D look: the tile grid, then the road's tiles filled in as tarmac. */
  private paintRoad(): void {
    const g = this.roadLayer;
    g.clear();
    if (is3D() || !this.sim) return;
    paintGrid(g);
    for (let y = 0; y < MAP_SIZE; y++) for (let x = 0; x < MAP_SIZE; x++) {
      if (!this.sim.isRoad(x, y)) continue;
      const pts = [toScreen(x, y), toScreen(x + 1, y), toScreen(x + 1, y + 1), toScreen(x, y + 1)].flat();
      g.poly(pts).fill({ color: 0x3a3833 });
    }
  }

  /** A number floating up from a point on the map: what a kill paid, what a leak cost. */
  private float(x: number, y: number, text: string, cls: string): void {
    if (this.floats.childElementCount > 24) this.floats.firstElementChild?.remove();
    const [wx, wy] = toScreen(x / TILE, y / TILE);
    const p = this.camera.world.toGlobal({ x: wx, y: wy });
    const f = el('span', `float ${cls}`, text);
    f.style.left = `${Math.round(p.x)}px`;
    f.style.top = `${Math.round(p.y - 24)}px`;
    this.floats.appendChild(f);
    setTimeout(() => f.remove(), 1100);
  }

  /** Pause while the menu is up, play on when it goes. */
  setPaused(on: boolean): void {
    this.paused = on;
  }

  // ---- input -----------------------------------------------------------------------------

  pointerDown(_wx: number, _wy: number, sx: number, sy: number, _button = 0): void {
    this.camera.beginPan(sx, sy);
  }

  pointerMove(wx: number, wy: number, sx: number, sy: number): void {
    if (this.camera.panning) this.camera.movePan(sx, sy);
    const [fx, fy] = toTile(wx, wy);
    const tx = Math.floor(fx), ty = Math.floor(fy);
    if (!this.hover || this.hover[0] !== tx || this.hover[1] !== ty) {
      this.hover = [tx, ty];
      if (this.placing) this.drawnSignature = '';
    }
  }

  pointerUp(): void {
    this.camera.endPan();
  }

  cancelGesture(): void {
    this.camera.endPan();
  }

  /** The top-left tile a tower of this type takes when the pointer is on (tx, ty). */
  private footprint(type: string, tx: number, ty: number): [number, number] {
    const size = DEFAULT_CONFIG.buildings[type]?.size ?? 1;
    return [tx - (size >> 1), ty - (size >> 1)];
  }

  tap(wx: number, wy: number, button: number, _at?: number): void {
    if (!this.sim || this.lost) return;
    if (this.scrubAt >= 0) { this.leaveScrub(); this.paused = false; this.renderPanel(); return; }
    if (button === 2) { this.clearIntent(); return; }
    const [fx, fy] = toTile(wx, wy);
    const tx = Math.floor(fx), ty = Math.floor(fy);
    if (this.aiming) {
      const def = DEFAULT_CONFIG.abilities[this.aiming];
      const ok = def.aim === 'tile' ? this.sim.ability(this.aiming, tx, ty) : this.sim.ability(this.aiming, 0, 0, this.sim.towerAt(tx, ty));
      if (!ok) { toast(def.aim === 'tile' ? 'Aim it at the map' : 'Click a damaged tower'); return; }
      haptic('medium');
      if (this.aiming === 'artillery') playLaunch();
      this.aiming = null;
      this.renderPanel();
      this.save();
      return;
    }
    if (this.placing) {
      const [gx, gy] = this.footprint(this.placing, tx, ty);
      const r = this.sim.place(this.placing, gx, gy);
      if (typeof r !== 'number') { toast(PLACE_ERROR_TEXT[r]); haptic('error'); return; }
      haptic('success');
      playCannon();
      // a mine is cheap and goes down in rows; anything else is placed once
      const again = !!DEFAULT_CONFIG.buildings[this.placing]?.mine && this.sim.cash >= (PRICE[this.placing] ?? Infinity);
      if (!again) this.placing = null;
      this.sel = { kind: 'tower', id: r };
      this.drawnSignature = '';
      this.renderPanel();
      this.save();
      return;
    }
    const tower = this.sim.towerAt(tx, ty);
    if (tower >= 0) { this.sel = { kind: 'tower', id: tower }; this.renderPanel(); return; }
    const unit = this.unitAt(fx, fy);
    if (unit >= 0) { this.sel = { kind: 'enemy', id: unit }; this.renderPanel(); return; }
    if (this.sel) { this.sel = null; this.renderPanel(); }
  }

  rightClick(_wx: number, _wy: number): void {
    this.clearIntent();
  }

  /** Puts down whatever was picked up: a tower to place, an ability to aim, a selection. */
  private clearIntent(): void {
    if (!this.placing && !this.aiming && !this.sel) return;
    this.placing = null;
    this.aiming = null;
    this.sel = null;
    this.drawnSignature = '';
    this.renderPanel();
  }

  key(k: string): void {
    if (k === 'Escape') this.clearIntent();
  }

  private unitAt(fx: number, fy: number): number {
    if (!this.next) return -1;
    const px = fx * TILE, py = fy * TILE, reach = (TILE * 3) / 4;
    let best = -1, bestD = Infinity;
    for (const u of this.next.units) {
      const d = Math.hypot(u.x - px, u.y - py);
      if (d <= reach && d < bestD) { best = u.id; bestD = d; }
    }
    return best;
  }

  blocksOrbit(): boolean {
    return false;
  }

  onViewChange(): void {
    this.drawnSignature = '';
    this.paintRoad();
  }

  /** Sends the next wave, by the button or the clock. */
  private sendWave(): void {
    if (!this.sim || this.lost) return;
    this.leaveScrub();
    const fresh = this.sim.phase === 'build';
    if (this.sim.startWave()) { if (fresh) this.record = [this.next]; haptic('medium'); this.autoAt = 0; this.renderPanel(); this.save(); }
  }

  /** Back to the live game from the scrub bar. */
  private leaveScrub(): void {
    if (this.scrubAt < 0) return;
    this.scrubAt = -1;
    this.prev = this.next = this.sim.snapshot();
    this.drawnSignature = '';
    this.renderPanel();
  }

  /** Shows the recorded tick at index i: the game stands still, paused, while the view looks back. */
  private scrubTo(i: number): void {
    if (this.record.length === 0) return;
    i = Math.max(0, Math.min(this.record.length - 1, i));
    this.paused = true;
    this.scrubAt = i;
    this.prev = this.record[Math.max(0, i - 1)]!;
    this.next = this.record[i]!;
    this.acc = 0;
    this.drawnSignature = '';
    this.liveShots = [];
    this.deaths = [];
    this.smoke = [];
    if (this.scrub) this.scrub.value = String(i);
    if (this.scrubLabel) this.scrubLabel.textContent = `${(i / TICKS_PER_SECOND).toFixed(1)}s of ${((this.record.length - 1) / TICKS_PER_SECOND).toFixed(1)}s`;
  }

  animate(): void { /* every frame goes through update */ }

  // ---- the loop ----------------------------------------------------------------------------

  private soundFor(sh: { by: string; type: string }): void {
    const t = sh.type;
    if (t === 'launch' || MISSILE_LAUNCHERS.has(t)) playLaunch();
    else if (t === 'impact' || t === 'selfdestruct') playIntercept();
    else if (t === 'fizzle' || t === 'repair') return;
    else if (t === 'icbm' || t === 'hgv_nuclear') playNuke();
    else if (t === 'ballistic' || t === 'hgv' || t === 'cruise_missile' || t === 'fpv_drone' || t === 'shahed' || t === 'attack_drone') playExplosion();
    else if (t === 'artillery' || t === 'howitzer') { playCannon(); playExplosion(); }
    else if (t === 'smoke') playLaunch();
    else if (t === 'land_mine' || t === 'at_mine') playExplosion();
    else if (t === 'sniper_nest' || t === 'marksman') playSniper();
    else if (t === 'tank' || t === 'destroyer' || t === 'boat' || t === 'landing_craft') playCannon();
    else if (t === 'f16') playLaunch();
    else if (t !== 'c_ram') playRifle();
  }

  private handleEvents(): void {
    for (const e of this.sim.tdEvents) {
      switch (e.kind) {
        case 'wave': feedNote(`WAVE ${e.n} · ${e.wave.name.toUpperCase()}`, 'threat', '▶'); playAlarmBell(); break;
        case 'leak': { feedNote(`${(DEFAULT_CONFIG.units[e.type]?.name ?? e.type).toUpperCase()} GOT THROUGH · −${e.lives}`, 'bad', '✹'); const hq = this.sim.towers[0]!; this.float((hq.x + 2) * TILE, (hq.y + 2) * TILE, `−${e.lives}`, 'bite'); break; }
        case 'cleared': feedNote(`WAVE ${e.n} HELD · +$${e.bonus}`, 'good', '✓'); playIntercept(); if (this.sim.phase === 'build') this.renderPanel(); break;
        case 'tower-lost': feedNote(`${(DEFAULT_CONFIG.buildings[e.type]?.name ?? e.type).toUpperCase()} DESTROYED`, 'bad', '✹'); if (this.sel?.kind === 'tower' && this.sel.id === e.id) this.renderPanel(); break;
        case 'won': recordWaves(this.map.id, this.sim.cleared); this.renderPanel(); break;
        case 'lost': this.lost = true; recordWaves(this.map.id, this.sim.cleared); clearSave(); playAlarmBell(); this.renderPanel(); break;
      }
    }
  }

  /** Called every frame by main. */
  update(deltaMS: number): void {
    if (!this.sim) return;
    if (!this.paused && !this.lost) this.acc += deltaMS * this.speed;
    while (this.acc >= TICK_MS && !this.lost) {
      this.sim.step();
      this.prev = this.next;
      const before = [...this.next.units, ...this.next.patrols];
      this.next = this.sim.snapshot();
      feedEvents(this.next.events);
      // a nuclear launch: the siren wails for the length of the fuse, and the loudspeaker calls it
      for (const e of this.next.events) {
        if (e.kind !== 'inbound' || !NUCLEAR.has(e.what)) continue;
        playSiren((DEFAULT_CONFIG.units[e.what]?.warhead?.fuseTicks ?? 5 * TICKS_PER_SECOND) / TICKS_PER_SECOND);
        announceNuke();
      }
      this.handleEvents();
      const now0 = performance.now();
      const alive = new Set([...this.next.units, ...this.next.patrols].map(u => u.id));
      const leaked = new Set(this.sim.tdEvents.filter(e => e.kind === 'leak').map(e => (e as { id: number }).id));
      const shotDown = new Set(this.next.events.filter(e => (e.kind === 'intercepted' || e.kind === 'downed') && e.id !== undefined).map(e => e.id!));
      for (const u of before) {
        if (alive.has(u.id) || leaked.has(u.id)) continue;
        // a missile that struck home made its own blast; a drone that dived made its
        const def = DEFAULT_CONFIG.units[u.type];
        if ((def?.domain === 'missile' || def?.oneWay) && !shotDown.has(u.id)) continue;
        // a ship of the fleet is never sunk: it sailed out of sight
        if (def?.domain === 'sea') continue;
        const air = def?.domain === 'aircraft' || def?.domain === 'drone' || def?.domain === 'missile';
        const kind = shotDown.has(u.id) || air ? 'debris' : FOOT.has(u.type) && !u.defender ? 'flesh' : 'machine';
        this.deaths.push({ x: u.x, y: u.y, kind, at: now0, seed: u.id * 31 + 7, type: u.type });
        if (kind === 'flesh') { playDeathCry(); playGore(); } else playExplosion();
      }
      this.deaths = this.deaths.filter(d => now0 - d.at < DEATH_MS);
      for (const u of this.next.units) if (u.type === 'cruise_missile' || u.fuse !== undefined) this.smoke.push({ x: u.x, y: u.y, at: now0, size: 3 });
      for (const c of this.next.clouds) {
        if (this.sim.tick % 3 !== 0) break;
        for (let k = 0; k < 2; k++) {
          const a = ((this.sim.tick * 7 + k * 180) % 360) * (Math.PI / 180);
          const d = (((this.sim.tick / 3 + k * 5) % 7) / 7) * c.radius * 0.9;
          this.smoke.push({ x: c.x + Math.cos(a) * d, y: c.y + Math.sin(a) * d, at: now0, size: 11, cloud: true });
        }
      }
      for (const sh of this.next.shots) {
        if (!MISSILE_LAUNCHERS.has(sh.type)) continue;
        this.smoke.push({ x: sh.fromX, y: sh.fromY, at: now0, size: 8, flash: true });
        for (let k = 1; k < 8; k++) this.smoke.push({ x: sh.fromX + (sh.toX - sh.fromX) * k / 8, y: sh.fromY + (sh.toY - sh.fromY) * k / 8, at: now0 + k * 60, size: 4 });
      }
      this.smoke = this.smoke.filter(p => now0 - p.at < SMOKE_MS);
      for (const sh of this.next.shots) {
        this.liveShots.push({ ...sh, at: now0 });
        this.soundFor(sh);
        if (sh.type === 'c_ram') {
          const b = this.next.buildings.find(x => x.hp > 0 && sh.fromX >= x.x * TILE && sh.fromX < (x.x + x.size) * TILE && sh.fromY >= x.y * TILE && sh.fromY < (x.y + x.size) * TILE);
          if (b) { this.firedAt.set(b.id, now0); cramFiring(b.id); }
        }
      }
      if (this.sim.phase === 'wave' && this.record.length < 20 * 60 * 6) this.record.push(this.next);
      this.acc -= TICK_MS;
      // the game is kept every few seconds of a wave, so a reload loses little
      if (this.sim.tick % (5 * TICKS_PER_SECOND) === 0) this.save();
    }
    const now = performance.now();
    // the next wave on the clock, once the last is held and nothing is waiting on the player
    if (this.auto && !this.lost && !this.paused && this.sim.phase === 'build' && (!this.sim.won || this.wonShown)) {
      if (this.autoAt === 0) { this.autoAt = now + AUTO_MS; this.renderPanel(); }
      else if (now >= this.autoAt) this.sendWave();
    }
    const list = snapshotToDrawables(this.next);
    for (const b of list) b.firing = now - (this.firedAt.get(b.seed ?? -1) ?? -Infinity) < 350;
    if (this.sel?.kind === 'tower') { const b = list.find(x => x.seed === this.sel!.id); if (b && b.state === 'normal') b.state = 'selected'; }
    const ghost = this.ghost();
    if (ghost) list.push(ghost);
    const sig = buildingsSignature(list);
    if (sig !== this.drawnSignature) { paintBuildings(this.buildingLayer, list, now, false); this.drawnSignature = sig; this.paintGhost(ghost); }
    paintBuildingFx(this.fxLayer, list, now);
    const t = this.lost || this.scrubAt >= 0 ? 1 : Math.min(this.acc / TICK_MS, 1);
    const selUnits = this.sel?.kind === 'enemy' ? new Set([this.sel.id]) : new Set<number>();
    paintUnits(this.unitLayer, this.prev, this.next, t, selUnits, now);
    paintBars(this.hud, this.camera.world, { prev: this.prev, next: this.next, t, selectedUnits: selUnits, selectedBuilding: this.sel?.kind === 'tower' ? this.sel.id : -1 });
    this.liveShots = this.liveShots.filter(sh => now - sh.at < shotLife(sh.type));
    paintShots(this.shotLayer, this.liveShots, now);
    paintSmoke(this.deathLayer, this.smoke, now);
    paintDeaths(this.deathLayer, this.deaths, now, false);
    drawIncoming(this.deathLayer, this.next.incoming, now);
    if (now - this.lastSync > 150) { this.lastSync = now; this.syncPanel(); this.syncConsole(); }
  }

  /** The tower being placed, where the pointer is, green or red. */
  private ghost(): DrawableBuilding | null {
    if (!this.placing || !this.hover) return null;
    const def = DEFAULT_CONFIG.buildings[this.placing];
    if (!def) return null;
    const [gx, gy] = this.footprint(this.placing, this.hover[0], this.hover[1]);
    const err = this.sim.canPlace(this.placing, gx, gy);
    return { type: this.placing, x: gx, y: gy, size: def.size, hpFrac: 1, seed: -1, state: err && err !== 'poor' ? 'ghost-bad' : 'ghost-ok' };
  }

  /** The range rings: the ghost's, and the selected tower's; and the road lit up while a mine is picked. */
  private paintGhost(ghost: DrawableBuilding | null): void {
    const g = this.ghostLayer;
    g.clear();
    if (this.placing && DEFAULT_CONFIG.buildings[this.placing]?.mine) {
      const W = 64;
      for (let y = 0; y < W; y++) for (let x = 0; x < W; x++) {
        if (!this.sim.isRoad(x, y)) continue;
        const pts = [toScreen(x, y), toScreen(x + 1, y), toScreen(x + 1, y + 1), toScreen(x, y + 1)].flat();
        g.poly(pts).fill({ color: 0xffd54a, alpha: 0.12 });
      }
    }
    if (ghost) {
      const def = DEFAULT_CONFIG.buildings[ghost.type]!;
      const range = def.mine ? def.mine.splash : def.patrol ? def.patrol.radius : rangeAt(def, 1);
      if (range) this.rangeRing(ghost.x + ghost.size / 2, ghost.y + ghost.size / 2, range, ghost.state === 'ghost-bad' ? 0xff6a5a : 0x7fd0ff);
      if (def.minRange) this.rangeRing(ghost.x + ghost.size / 2, ghost.y + ghost.size / 2, def.minRange, 0xff6a5a);
    }
    if (this.sel?.kind === 'tower') {
      const tw = this.sim.towers[this.sel.id];
      if (tw && !tw.gone && tw.id !== 0) {
        const range = tw.def.mine ? tw.def.mine.splash : tw.def.patrol ? tw.def.patrol.radius : rangeAt(tw.def, tw.level);
        if (range) this.rangeRing(tw.x + tw.def.size / 2, tw.y + tw.def.size / 2, range, 0xffd54a);
        if (tw.def.minRange) this.rangeRing(tw.x + tw.def.size / 2, tw.y + tw.def.size / 2, tw.def.minRange, 0xff6a5a);
      }
    }
  }

  private rangeRing(tx: number, ty: number, range: number, color: number): void {
    const r = range / TILE;
    // a gun that reaches the whole map has no ring worth drawing
    if (r >= MAP_SIZE) return;
    const pts: number[] = [];
    for (let i = 0; i < 64; i++) {
      const a = (i / 64) * Math.PI * 2;
      const [sx, sy] = toScreen(tx + r * Math.cos(a), ty + r * Math.sin(a));
      pts.push(sx, sy);
    }
    this.ghostLayer.poly(pts).fill({ color, alpha: 0.035 }).stroke({ color, width: 1.5, alpha: 0.85 });
  }

  private syncConsole(): void {
    hideTag();
    if (this.sel?.kind === 'enemy') {
      const u = this.next.units.find(x => x.id === this.sel!.id);
      if (u) { showUnit(u); return; }
      this.sel = null;
      this.renderPanel();
    }
    hideConsole();
  }

  // ---- the HUD ----------------------------------------------------------------------------

  private speedNext(): (typeof SPEEDS)[number] {
    return SPEEDS[(SPEEDS.indexOf(this.speed) + 1) % SPEEDS.length]!;
  }

  /** The numbers that change every moment, written into the panel that is already there. */
  private syncPanel(): void {
    const p = this.panelParts;
    if (!p || !this.sim) return;
    const sim = this.sim;
    p.cash.textContent = `$${sim.cash}`;
    const n = sim.phase === 'wave' ? sim.wave : sim.wave + 1;
    const w = sim.waveAt(n);
    p.wave.textContent = `Wave ${n}${sim.won ? '' : ` of ${this.map.waves}`} · ${w.name}`;
    p.sub.textContent = sim.phase === 'wave' ? (sim.remaining > 0 ? `${sim.remaining} still to come · ${this.next.units.length} on the road` : `${this.next.units.length} on the road`) : w.briefing;
    // what the next wave brings, as icons and counts
    const counts = new Map<string, number>();
    const coming = sim.waveAt(sim.phase === 'wave' ? sim.wave + 1 : n);
    for (const g of coming.groups) counts.set(g.unit, (counts.get(g.unit) ?? 0) + g.count);
    // and what the fleet launches, after a wave break, by the ship that launches it
    const fleet = new Map<string, number>();
    for (const sh of coming.ships) fleet.set(sh.ship, (fleet.get(sh.ship) ?? 0) + 1);
    const key = `${sim.phase === 'wave' ? 'next' : 'now'}:${[...counts].map(([u, c]) => `${u}=${c}`).join(',')}|${[...fleet].map(([u, c]) => `${u}=${c}`).join(',')}`;
    if (p.preview.dataset.key !== key) {
      p.preview.dataset.key = key;
      p.preview.replaceChildren();
      if (sim.phase === 'wave') p.preview.append('Next: ');
      for (const [u, c] of counts) {
        const item = el('span', 'pv');
        item.append(portraitEl(u, 'pico'), `×${c}`);
        item.title = DEFAULT_CONFIG.units[u]?.name ?? u;
        p.preview.appendChild(item);
      }
      if (fleet.size > 0) p.preview.append(' · at sea: ');
      for (const [u, c] of fleet) {
        const item = el('span', 'pv');
        item.append(portraitEl(u, 'pico'), `×${c}`);
        const launches = coming.ships.filter(sh => sh.ship === u).reduce((t, sh) => t + sh.count, 0);
        item.title = `${DEFAULT_CONFIG.units[u]?.name ?? u}: launches ${launches} at the Command Center`;
        p.preview.appendChild(item);
      }
    }
    if (p.start && this.autoAt > 0 && sim.phase === 'build') {
      const left = Math.max(0, Math.ceil((this.autoAt - performance.now()) / 1000));
      const text = `▶ Wave ${sim.wave + 1} in ${left}…`;
      const lbl = p.start.querySelector('.lbl') ?? p.start;
      if (lbl.textContent !== text) lbl.textContent = text;
    }
    p.integrity.textContent = `${sim.integrity} / ${sim.maxIntegrity}`;
    const frac = sim.integrity / sim.maxIntegrity;
    p.bar.style.width = `${Math.round(frac * 100)}%`;
    p.bar.className = frac > 0.5 ? 'ok' : frac > 0.25 ? 'hurt' : 'low';
    // a tower the cash does not cover cannot be picked up; one already in hand is put down when the cash runs out
    for (const [type, b] of p.shop) {
      const poor = sim.cash < (PRICE[type] ?? 0);
      b.classList.toggle('poor', poor);
      b.disabled = poor || this.lost;
    }
    if (this.placing && sim.cash < (PRICE[this.placing] ?? 0)) { this.placing = null; this.drawnSignature = ''; this.renderPanel(); return; }
    for (const [kind, b] of p.abilities) {
      const left = sim.abilityReadyIn(kind);
      const total = DEFAULT_CONFIG.abilities[kind].cooldownTicks;
      b.disabled = left > 0 || this.lost;
      b.classList.toggle('cooling', left > 0);
      b.style.setProperty('--p', String(1 - left / total));
      const sub = b.querySelector('.sub');
      if (sub) sub.textContent = left > 0 ? `${Math.ceil(left / TICKS_PER_SECOND)}s` : 'ready';
    }
    if (this.sel?.kind === 'tower') {
      const t = sim.towers[this.sel.id];
      if (t && p.selHp) p.selHp.textContent = t.gone ? 'sold' : t.alive ? `${t.hp} / ${t.maxHp} hp${Number.isFinite(t.ammo) ? ` · ${t.ammo} rounds` : ''}` : 'destroyed';
      if (t && p.upgrade) { const c = sim.upgradeCost(t.id); p.upgrade.disabled = c === null || sim.cash < c; }
      if (t && p.rebuild) { const c = sim.rebuildCost(t.id); p.rebuild.disabled = c === null || sim.cash < c; }
    }
  }

  renderPanel(): void {
    const p = this.panel;
    p.replaceChildren();
    if (!this.sim) return;
    const sim = this.sim;
    const parts: NonNullable<typeof this.panelParts> = { cash: el('span'), wave: el('b'), sub: el('span'), hint: el('span'), integrity: el('span'), bar: el('i'), shop: new Map(), abilities: new Map(), selHp: null, upgrade: null, rebuild: null, preview: el('span', 'd-preview'), start: null };
    this.panelParts = parts;

    // the top bar: cash, the wave, the Command Center
    const top = el('div', 'd-top panel-card');
    const cash = el('div', 'd-cash');
    cash.append(el('span', 'd-lbl', 'Cash'), parts.cash);
    const wave = el('div', 'd-wave');
    parts.wave.className = 'd-wave-name';
    parts.sub.className = 'd-wave-sub';
    parts.hint.className = 'd-hint';
    parts.preview.title = 'What the next wave brings';
    wave.append(parts.wave, parts.sub, parts.preview, parts.hint);
    const hq = el('div', 'd-hq');
    const hqTop = el('div', 'd-hq-top');
    hqTop.append(el('span', 'lbl', 'Command Center'), parts.integrity);
    const bar = el('i', 'bar');
    bar.appendChild(parts.bar);
    hq.append(hqTop, bar);
    top.append(cash, wave, hq);
    p.appendChild(top);
    if (this.placing) parts.hint.textContent = `Click the map to place · right-click or Esc to put it down`;
    else if (this.aiming) parts.hint.textContent = DEFAULT_CONFIG.abilities[this.aiming].aim === 'tile' ? 'Click a tile to aim it · Esc to put it down' : 'Click a damaged tower · Esc to put it down';

    // the shop, down the right
    const shop = el('div', 'd-shop panel-card');
    shop.appendChild(el('div', 'ph', 'Towers'));
    const grid = el('div', 'd-tiles');
    TOWERS.forEach((type, i) => {
      const def = DEFAULT_CONFIG.buildings[type]!;
      const b = button(def.name, () => {
        if (this.lost || sim.cash < (PRICE[type] ?? 0)) return;
        this.placing = this.placing === type ? null : type;
        this.aiming = null;
        this.sel = null;
        this.drawnSignature = '';
        this.renderPanel();
      }, { cls: 'tile', iconEl: portraitEl(type), sub: `$${PRICE[type]}`, active: this.placing === type, disabled: this.lost || sim.cash < (PRICE[type] ?? 0), title: `${def.real ? `${def.real} · ` : ''}${ROLE[type] ?? ''}`, ...(i < 9 ? { key: String(i + 1) } : {}) });
      parts.shop.set(type, b);
      grid.appendChild(b);
    });
    shop.appendChild(grid);
    p.appendChild(shop);

    // the abilities, down the left
    const abilities = el('div', 'r-left abilities panel-card');
    abilities.appendChild(el('div', 'ph', 'Support'));
    for (const kind of ABILITY_KINDS) {
      const def = DEFAULT_CONFIG.abilities[kind];
      const b = button(def.name, () => {
        if (this.lost || sim.abilityReadyIn(kind) > 0) return;
        this.aiming = this.aiming === kind ? null : kind;
        this.placing = null;
        this.drawnSignature = '';
        this.renderPanel();
      }, { cls: 'tile', icon: abilityIcon(kind), sub: 'ready', active: this.aiming === kind, title: def.role, key: kind === 'artillery' ? 'Q' : kind === 'smoke' ? 'W' : 'E' });
      parts.abilities.set(kind, b);
      abilities.appendChild(b);
    }
    p.appendChild(abilities);

    // what is selected, bottom left
    if (this.sel?.kind === 'tower') {
      const t = sim.towers[this.sel.id];
      if (t && !t.gone) p.appendChild(this.towerCard(t, parts));
    }

    // the controls, bottom right
    const actions = el('div', 'r-actions');
    if (!this.lost) {
      const early = sim.phase === 'wave';
      const label = early ? `Call wave ${sim.wave + 1} early` : sim.wave === 0 ? 'Start' : `Wave ${sim.wave + 1}`;
      const start = button(label, () => this.sendWave(), { cls: early ? 'big' : 'primary big', icon: early ? 'forward' : 'play', key: 'Space', title: early ? `Sends the next wave now, for +$${Math.floor((waveBonus(sim.wave) * EARLY_CALL_BONUS_PCT) / 100)}` : 'Sends the next wave' });
      parts.start = start;
      actions.appendChild(start);
      const row = el('div', 'd-row');
      row.appendChild(button(`Auto: ${this.auto ? 'on' : 'off'}`, () => { this.auto = !this.auto; this.autoAt = 0; this.renderPanel(); }, { cls: `small${this.auto ? ' active' : ''}`, key: 'A', title: 'The next wave comes by itself a few seconds after the last is held' }));
      const next = this.speedNext();
      const sp = button(`${this.speed}×`, () => { this.speed = next; this.renderPanel(); }, { cls: `small${this.speed > 1 ? ' active' : ''}`, key: 'F', title: `Speed: click for ${next}×` });
      row.appendChild(sp);
      row.appendChild(button(this.paused ? 'Resume' : 'Pause', () => { if (this.paused) { this.leaveScrub(); this.paused = false; } else this.paused = true; this.renderPanel(); }, { cls: `small${this.paused ? ' active' : ''}`, icon: this.paused ? 'play' : 'pause', iconOnly: true, key: 'P', title: this.paused ? 'Resume' : 'Pause, and look back through the wave' }));
      actions.appendChild(row);
      // paused with a wave on record: a bar to drag back through it, tick by tick
      if (this.paused && this.record.length > 1) {
        const bar = el('div', 'r-scrub');
        const range = el('input');
        range.type = 'range';
        range.min = '0';
        range.max = String(this.record.length - 1);
        range.step = '1';
        range.value = String(this.scrubAt >= 0 ? this.scrubAt : this.record.length - 1);
        range.setAttribute('aria-label', 'Look back through the wave');
        range.oninput = () => this.scrubTo(Number(range.value));
        const label = el('span', 'stats', this.scrubAt >= 0 ? '' : 'drag to look back');
        bar.append(icon('rewind', 'ico muted'), range, label);
        this.scrub = range;
        this.scrubLabel = label;
        p.appendChild(bar);
        if (this.scrubAt >= 0) this.scrubTo(this.scrubAt);
      } else { this.scrub = null; this.scrubLabel = null; }
    }
    actions.appendChild(button('Menu', () => this.cb.onHome(), { cls: 'home', icon: 'menu', key: 'H' }));
    p.appendChild(actions);

    // the result: the fall, or the map held
    if (this.lost) p.appendChild(this.resultCard(false));
    else if (sim.won && !this.wonShown) p.appendChild(this.resultCard(true));
    this.syncPanel();
  }

  private towerCard(t: Tower, parts: NonNullable<typeof this.panelParts>): HTMLElement {
    const sim = this.sim;
    const card = el('div', 'v-sel');
    const head = el('div', 'sel-head');
    head.appendChild(portraitEl(t.type, 'sel-ico'));
    const title = el('div', 'sel-title');
    const name = el('b', '', t.def.name);
    if (t.id !== 0 && !t.def.mine) name.append(' ', el('span', 'stars', '★'.repeat(t.level) + '☆'.repeat(MAX_TOWER_LEVEL - t.level)));
    title.appendChild(name);
    if (t.def.real) title.appendChild(el('span', 'muted', t.def.real));
    const hp = el('span', 'muted');
    parts.selHp = hp;
    title.appendChild(hp);
    head.appendChild(title);
    // Escape lands here while a card is up, and must still put down a row of mines being laid:
    // the mine just placed is the card, and placing it is still on
    head.appendChild(button('Deselect', () => this.clearIntent(), { cls: 'x', icon: 'x', iconOnly: true, title: 'Deselect', key: 'Escape' }));
    card.appendChild(head);
    if (t.id === 0) {
      card.appendChild(el('p', 'sel-facts', `What the enemy must wear down. Everything that gets through the road takes a bite; a warhead takes a big one. Emergency repair puts ${DEFAULT_CONFIG.abilities.repair.repairPct} percent back.`));
      return card;
    }
    card.appendChild(el('p', 'sel-facts', ROLE[t.type] ?? ''));
    const facts = el('p', 'sel-facts stats', weaponLine(t));
    const targets = t.def.targets ?? ['infantry', 'vehicle'];
    facts.append(' · ', targets.map(x => THREAT_NAMES[x]).join(', '));
    card.appendChild(facts);
    if (!t.def.mine && !t.def.patrol && t.alive) {
      const prio = el('div', 'priority');
      prio.appendChild(el('span', 'sel-label', 'Shoot first'));
      const chips = el('div', 'chips');
      for (const m of TARGET_MODES) chips.appendChild(button(TARGET_MODE_NAMES[m], () => { sim.setMode(t.id, m as TargetMode); this.renderPanel(); this.save(); }, { cls: 'chip', active: t.mode === m }));
      prio.appendChild(chips);
      card.appendChild(prio);
    }
    const row = el('div', 'sel-buttons');
    if (!t.alive) {
      const cost = sim.rebuildCost(t.id);
      const b = button('Rebuild', () => { if (sim.rebuild(t.id)) { haptic('success'); this.renderPanel(); this.save(); } }, { cls: 'upgrade', icon: 'hammer', sub: cost !== null ? `$${cost}` : '', key: 'U' });
      parts.rebuild = b;
      row.appendChild(b);
    } else if (!t.def.mine) {
      const cost = sim.upgradeCost(t.id);
      const b = button(cost === null ? 'Top level' : `Upgrade to ${'★'.repeat(t.level + 1)}`, () => { if (sim.upgrade(t.id)) { haptic('success'); playIntercept(); this.drawnSignature = ''; this.renderPanel(); this.save(); } },
        { cls: 'upgrade', icon: 'up', sub: cost !== null ? `$${cost} · +damage, +range, +hp` : 'as good as it gets', key: 'U', disabled: cost === null });
      parts.upgrade = b;
      row.appendChild(b);
    }
    row.appendChild(button(`Sell for $${sim.sellValue(t.id)}`, () => { sim.sell(t.id); this.sel = null; this.drawnSignature = ''; this.renderPanel(); this.save(); }, { cls: 'remove', key: 'X' }));
    card.appendChild(row);
    return card;
  }

  private resultCard(won: boolean): HTMLElement {
    const sim = this.sim;
    const card = el('div', `r-result${won ? '' : ' bad'}`);
    const topRow = el('div', 'result-top');
    topRow.appendChild(el('h2', '', won ? 'Map held' : 'Command Center fallen'));
    card.appendChild(topRow);
    const best = bestWaves(this.map.id);
    card.appendChild(el('p', '', won
      ? `${this.map.name}: all ${this.map.waves} waves held with ${sim.integrity} integrity to spare. The waves go on from here, bigger each time, for the record.`
      : `${this.map.name}: ${sim.cleared} wave${sim.cleared === 1 ? '' : 's'} held, ${sim.kills} kills, ${sim.leaks} through. ${best > sim.cleared ? `Your best on ${this.map.name.toLowerCase()} is ${best}.` : `A new best on ${this.map.name.toLowerCase()}.`}`));
    const foot = el('div', 'result-buttons');
    if (won) foot.appendChild(button('Keep going', () => { this.wonShown = true; this.autoAt = 0; this.renderPanel(); }, { cls: 'primary big', key: 'Enter' }));
    else foot.appendChild(button('Try again', () => this.start(this.map), { cls: 'primary big', icon: 'retry', key: 'R' }));
    foot.appendChild(button('Menu', () => this.cb.onHome(), { cls: 'home', icon: 'menu' }));
    card.appendChild(foot);
    return card;
  }
}

/** The name an enemy goes by in the feed and the console. */
export const enemyName = (type: string): string => DEFAULT_CONFIG.units[type]?.name ?? type;
